import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Copy, AlertTriangle } from "lucide-react";

/**
 * Settings section that lets the signed-in user enable / disable TOTP 2FA
 * and manage their backup codes.
 *
 * `user`     – the current user record (already has totp_enabled + backup_codes_remaining)
 * `onChanged` – refresh callback (typically AuthContext.checkAuth)
 */
export default function TwoFactorSection({ user, onChanged }) {
  const enabled = !!user?.totp_enabled;
  const remaining = user?.backup_codes_remaining ?? 0;

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-2fa">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-teal-700" />
        <h2 className="font-heading text-xl font-bold">Two-factor authentication</h2>
        {enabled && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-widest font-semibold" data-testid="tfa-status-on">
            <ShieldCheck className="h-3 w-3" /> On
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Require a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password) on every sign-in.
        You can trust a device for 30 days at the code prompt.
      </p>

      {!enabled && (
        <div className="mt-6">
          <Button
            onClick={() => setEnrollOpen(true)}
            data-testid="tfa-enable-btn"
            className="rounded-full h-10 px-6 bg-teal-700 hover:bg-teal-800"
          >
            <ShieldCheck className="h-4 w-4 mr-1.5" /> Enable two-factor
          </Button>
        </div>
      )}

      {enabled && (
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <div className="rounded-md border border-slate-200 px-4 py-3">
            <div className="label-caps text-slate-500">Backup codes left</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="tfa-backup-count">{remaining}</div>
            <div className="text-xs text-slate-500 mt-1">
              {remaining <= 2
                ? "Regenerate a fresh set soon — old codes stop working."
                : "Kept safe in case you lose your phone."}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 px-4 py-3 md:col-span-2 flex flex-wrap items-center gap-3 justify-between">
            <div className="text-sm text-slate-600">Need a fresh set? The old ones become invalid immediately.</div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setRegenOpen(true)}
                data-testid="tfa-regenerate-btn"
                className="rounded-full h-9 px-4 border-teal-200 text-teal-800 hover:bg-teal-50"
              >
                <KeyRound className="h-3.5 w-3.5 mr-1.5" /> New backup codes
              </Button>
              <Button
                variant="outline"
                onClick={() => setDisableOpen(true)}
                data-testid="tfa-disable-btn"
                className="rounded-full h-9 px-4 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                Turn off
              </Button>
            </div>
          </div>
        </div>
      )}

      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} onEnabled={onChanged} />
      <PasswordDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Turn off two-factor authentication"
        description="Confirm your password. Your trusted-device cookie will also be revoked."
        confirmLabel="Turn off 2FA"
        endpoint="/auth/2fa/disable"
        testid="tfa-disable-dialog"
        onDone={onChanged}
      />
      <RegenerateDialog open={regenOpen} onClose={() => setRegenOpen(false)} onDone={onChanged} />
    </section>
  );
}


function EnrollDialog({ open, onClose, onEnabled }) {
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  const start = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll");
      setQr(data.qr_image);
      setManualKey(data.manual_key);
    } catch (e) {
      toast.error("Could not start 2FA setup", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setQr(null);
      setManualKey(null);
      setCode("");
      setBackupCodes(null);
      start();
    }
  }, [open, start]);

  const verify = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-enrollment", { code });
      setBackupCodes(data.backup_codes);
      toast.success("Two-factor authentication is on");
      onEnabled && onEnabled();
    } catch (e) {
      toast.error("That code didn't match", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    setBackupCodes(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="tfa-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {backupCodes ? "Save your backup codes" : "Set up two-factor authentication"}
          </DialogTitle>
          <DialogDescription>
            {backupCodes
              ? "You won't see these again. Store them somewhere safe — each one is a single-use fallback if you lose your phone."
              : "Scan the QR code with Google Authenticator, Authy or 1Password, then enter the 6-digit code below."}
          </DialogDescription>
        </DialogHeader>

        {!backupCodes && (
          <div className="space-y-4">
            {loading && <div className="text-slate-500 text-sm">Generating QR code…</div>}
            {!loading && qr && (
              <>
                <div className="flex justify-center">
                  <img src={qr} alt="Scan to enrol in 2FA" className="h-48 w-48 rounded border border-slate-200" data-testid="tfa-qr-image" />
                </div>
                {manualKey && (
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs">
                    <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-1">Can&apos;t scan? Enter this key manually</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 font-mono break-all text-slate-800">{manualKey}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard?.writeText(manualKey); toast.success("Copied"); }}
                        className="h-7 w-7 rounded hover:bg-slate-200 text-slate-500 flex items-center justify-center"
                        data-testid="tfa-copy-key"
                        aria-label="Copy manual key"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="label-caps">6-digit code</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="tfa-enroll-code"
                    placeholder="123456"
                    className="font-mono tracking-widest text-center"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {backupCodes && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="text-xs font-semibold uppercase tracking-widest">Save these now</div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-slate-900" data-testid="tfa-backup-codes">
              {backupCodes.map((c) => (
                <div key={c} className="rounded bg-white border border-amber-200 px-3 py-1.5 text-center tabular-nums">{c}</div>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard?.writeText(backupCodes.join("\n")); toast.success("Copied all codes"); }}
              className="mt-4 w-full rounded-full h-9 border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="tfa-copy-backup-codes"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy all
            </Button>
          </div>
        )}

        <DialogFooter>
          {!backupCodes ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={verify}
                disabled={busy || code.length !== 6}
                data-testid="tfa-enroll-verify"
                className="bg-teal-700 hover:bg-teal-800"
              >
                {busy ? "Verifying…" : "Verify & enable"}
              </Button>
            </>
          ) : (
            <Button onClick={finish} data-testid="tfa-enroll-done" className="bg-teal-700 hover:bg-teal-800">
              I&apos;ve saved them
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PasswordDialog({ open, onClose, title, description, confirmLabel, endpoint, testid, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setPassword(""); }, [open]);

  const submit = async () => {
    if (!password) return;
    setBusy(true);
    try {
      await api.post(endpoint, { password });
      toast.success(confirmLabel);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error("Could not confirm", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-sm" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="label-caps">Your password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={`${testid}-password`}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !password}
            data-testid={`${testid}-submit`}
            className="bg-rose-600 hover:bg-rose-700"
          >
            {busy ? "Confirming…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RegenerateDialog({ open, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  useEffect(() => { if (!open) { setPassword(""); setCodes(null); } }, [open]);

  const submit = async () => {
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { password });
      setCodes(data.backup_codes);
      toast.success("New backup codes generated — old ones no longer work");
      onDone && onDone();
    } catch (e) {
      toast.error("Could not regenerate", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="tfa-regen-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Regenerate backup codes</DialogTitle>
          <DialogDescription>
            {codes ? "Save these now — you won't see them again." : "Confirm your password to receive a fresh set. Any old codes stop working immediately."}
          </DialogDescription>
        </DialogHeader>
        {!codes && (
          <div className="space-y-1.5">
            <Label className="label-caps">Your password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="tfa-regen-password" autoFocus />
          </div>
        )}
        {codes && (
          <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-900" data-testid="tfa-regen-codes">
            {codes.map((c) => (
              <div key={c} className="rounded bg-white border border-amber-200 px-3 py-1.5 text-center tabular-nums">{c}</div>
            ))}
          </div>
        )}
        <DialogFooter>
          {!codes ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !password} data-testid="tfa-regen-submit" className="bg-teal-700 hover:bg-teal-800">
                {busy ? "Generating…" : "Generate"}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="tfa-regen-done" className="bg-teal-700 hover:bg-teal-800">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
